/**
 * Indonesian message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: Anda (Google's Indonesian UI register,
 * capital A; locked in l10n/locales.md).
 *
 * Platform terms follow Indonesian UI conventions (setelan for settings,
 * tema for theme, percakapan for conversation, penyandingan/sandingkan
 * for pairing, hapus for dismiss, pencerminan for mirror). "Glance" is
 * the untranslated feature brand name (glossary D1); "papan klip" /
 * "klip" render the clipboard / D2 clip terms; "penerima" renders the
 * receiving-endpoint sense of "surface".
 *
 * Plural notes: Indonesian is an `other`-only CLDR locale — nouns take no
 * grammatical plural (no reduplication in UI: "perangkat", not
 * "perangkat-perangkat"), so every `selectPlural("id", …)` call supplies
 * only an `other` form (the `one` key is unused and omitted).
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Terhubung",
    statusReconnecting: "Menghubungkan kembali…",
    statusDisconnected: "Terputus",
    openSettings: "Buka setelan",
    pairedFallbackDevice: "ponsel Anda",
    publicModeToggleEnable: "Sembunyikan konten notifikasi",
    publicModeToggleDisable: "Tampilkan konten notifikasi",
  },

  notifications: {
    replyAriaLabel: "Balas",
    replyPlaceholder: "Balas…",
    replySend: "Kirim",
    sent: "Terkirim",
    failedToSend: "Gagal mengirim",
    unread: "Belum dibaca",
    dismiss: "Hapus",
    notificationImageAlt: "Gambar notifikasi",
  },

  conversations: {
    replyAriaLabel: "Balas",
    replyPlaceholder: "Balas…",
    replySend: "Kirim",
    sent: "Terkirim",
    failedToSend: "Gagal mengirim",
    dismiss: "Hapus percakapan",
    messagesListLabel: "Pesan dalam percakapan ini",
    deviceOwner: "Anda",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Tidak ada pesan belum dibaca"
        : `${count} ${selectPlural("id", count, { other: "pesan belum dibaca" })}`,
  },

  list: {
    today: "Hari ini",
    yesterday: "Kemarin",
    searchNotifications: "Cari notifikasi",
    clearSearch: "Hapus pencarian",
    clearAll: "Hapus semua",
    markAllRead: "Tandai semua dibaca",
    groupCheckboxLabel: "Kelompokkan",
    noNotificationsTitle: "Belum ada notifikasi",
    noNotificationsDesc: "Pesan baru yang dicerminkan dari ponsel Anda akan muncul di sini.",
    noMatchesTitle: "Tidak ada yang cocok",
    notificationsList: "Notifikasi",
    privacyAppList: "Notifikasi menurut aplikasi",
    clear: "Hapus",
    clearMatches: (count: number): string =>
      `Hapus ${count} ${selectPlural("id", count, { other: "hasil" })}`,
    noMatchesDesc: (query: string): string => `Tidak ada notifikasi yang cocok dengan “${query}”.`,
    appNotificationsGroup: (appName: string): string => `Notifikasi ${appName}`,
    dismissAllFrom: (appName: string): string => `Hapus semua dari ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("id", count, { other: "pesan" })
          : kind === "calls"
            ? selectPlural("id", count, { other: "panggilan" })
            : selectPlural("id", count, { other: "notifikasi" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "hapus",
    kbdNavigate: "navigasi",
    kbdOrSeparator: "atau",
  },

  settings: {
    back: "Kembali ke notifikasi",
    settings: "Setelan",
    general: "Umum",
    theme: "Tema",
    themeAuto: "Otomatis",
    themeLight: "Terang",
    themeDark: "Gelap",
    language: "Bahasa",
    languageSystemDefault: "Default sistem",
    notificationsSection: "Notifikasi",
    osNotifications: "Notifikasi OS",
    callNotifications: "Notifikasi panggilan",
    privacySecurity: "Privasi",
    analyticsToggle: "Statistik penggunaan anonim",
    analyticsToggleDescription: "Hanya jumlah dan pencapaian — bukan notifikasi Anda.",
    statusConnected: "Terhubung",
    statusReconnecting: "Menghubungkan kembali…",
    statusDisconnected: "Terputus",
    disconnectFromPhone: "Putuskan sambungan dari ponsel",
    disconnectConfirm: "Anda yakin? Ini akan membatalkan penyandingan perangkat Anda.",
    yesDisconnect: "Ya, putuskan sambungan",
    cancel: "Batal",
    version: "Versi",
    encryption: "Enkripsi",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Butuh bantuan? Email __EMAIL__",
    disconnectFailed: "Gagal memutuskan sambungan — silakan coba lagi",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Sambungan diputus, tetapi pembersihan lokal gagal: ${errorMsg}. Silakan muat ulang halaman.`,
    disconnectLocalCleanupFailed:
      "Sambungan diputus, tetapi pembersihan lokal gagal. Silakan muat ulang halaman.",
  },

  pairing: {
    enterCodeInstruction: "Masukkan kode ini di aplikasi Reflecto",
    pairedSuccessfully: "Berhasil disandingkan!",
    pairingFailed: "Penyandingan gagal",
    requestingPairingCode: "Meminta kode penyandingan…",
    codeExpired: "Kode kedaluwarsa",
    tryAgain: "Coba Lagi",
    failedToStartPairing: "Gagal memulai penyandingan",
    installHeading: "Belum punya aplikasinya?",
    installScanHint: "Pindai dengan kamera ponsel Anda",
    installQrLabel: "Kode QR untuk memasang Reflecto di ponsel Anda",
    capabilitiesHeading: "Setelah disandingkan, Anda bisa:",
    capabilityMirror: "Melihat notifikasi ponsel di sini",
    capabilityReply: "Membalas tanpa mengambil ponsel",
    capabilityShare: "Berbagi papan klip dan berkas dua arah",
    encryptionNote: "Terenkripsi end-to-end. Server tidak pernah melihat konten Anda.",
  },

  dnd: {
    title: "Jangan Ganggu",
    description: "Jeda notifikasi di perangkat ini selama satu jam ke depan.",
    snooze1h: "Jeda 1 jam",
    snoozeActiveUntil: (formattedTime: string): string => `Aktif hingga ${formattedTime}`,
    cancelSnooze: "Batal",
  },

  connectors: {
    title: "Layanan terhubung",
    emptyBody:
      "Belum ada layanan terhubung. Hubungkan ChatGPT atau Claude dari setelan konektornya, lalu setujui kodenya di ponsel Anda.",
    scopeSend: "Dapat mengirim notifikasi",
    added: (date: string): string => `Ditambahkan ${date}`,
    lastUsed: (relative: string): string => `Terakhir digunakan ${relative}`,
    neverUsed: "Tidak pernah digunakan",
    revoke: "Putuskan sambungan",
    revokeConfirm: (label: string): string =>
      `Putuskan sambungan ${label}? Layanan ini akan kehilangan akses seketika.`,
    revokeAction: "Putuskan sambungan",
    revokeConfirmLabel: (label: string): string => `Putuskan sambungan ${label}`,
    cancel: "Batal",
    loadError: "Tidak dapat memuat layanan terhubung. Silakan coba lagi.",
    revokeError: "Tidak dapat memutuskan sambungan layanan. Silakan coba lagi.",
    retry: "Coba lagi",
  },

  safetyNumber: {
    rowLabel: "Verifikasi enkripsi",
    rowSublabel: "Bandingkan nomor keamanan dengan ponsel Anda",
    cardTitle: "Nomor keamanan",
    caption: (deviceLabel: string): string =>
      `Bandingkan dengan nomor yang ditampilkan di ${deviceLabel}. Jika keduanya cocok, enkripsi end-to-end Anda utuh.`,
    repairNote: "Nomor ini berubah saat Anda menyandingkan ulang perangkat.",
    copy: "Salin",
    copied: "Disalin",
    maskedHint: "Tersembunyi saat mode privasi aktif",
  },

  feedback: {
    title: "Kirim masukan",
    close: "Tutup",
    messageLabel: "Pesan Anda",
    messagePlaceholder: "Menemukan bug, punya ide, atau ada yang terasa janggal? Beri tahu kami.",
    categoryLabel: "Kategori",
    categoryBug: "Bug",
    categoryIdea: "Ide",
    categoryOther: "Lainnya",
    emailLabel: "Email (opsional, hanya jika Anda ingin balasan)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Kami tidak akan menambahkan Anda ke daftar apa pun.",
    disclosure:
      "Pesan Anda dikirim ke server Reflecto dan dicatat di pelacak masalah internal kami. Kami melampirkan versi aplikasi Anda dan sebuah ID perangkat yang ditetapkan saat penyandingan agar kami dapat mengaitkan laporan. Kami tidak melihat notifikasi, kontak, atau data pribadi Anda. Email bersifat opsional — cantumkan hanya jika Anda ingin balasan. Kami tidak menambahkan Anda ke daftar apa pun.",
    submit: "Kirim masukan",
    sending: "Mengirim…",
    success: "Terima kasih — dibaca oleh manusia, biasanya dalam 24 jam",
    error: "Tidak dapat mengirim. Silakan coba lagi.",
    done: "Selesai",
    sendFeedback: "Kirim masukan",
  },

  uninstall: {
    headline: "Ekstensi Reflecto telah dicopot",
    body: "Tidak pernah ada akun, dan ekstensi menyimpan penyandingan Anda di komputer ini, jadi tidak ada yang perlu Anda bersihkan.",
    ask: "Jika Anda punya tiga puluh detik, kami ingin tahu apa yang salah.",
    categoryLegend: "Ini masukan jenis apa?",
    categoryBug: "Ada yang rusak",
    categoryIdea: "Ada yang saya butuhkan tapi tidak tersedia",
    categoryOther: "Hal lain",
    messageLabel: "Apa yang salah?",
    messagePlaceholder: "Notifikasi berhenti masuk setelah beberapa jam.",
    messageRequired: "Tulis satu kalimat dulu, lalu kirim.",
    charactersLeft: (count: number): string => `Sisa ${count} karakter`,
    emailLabel: "Email (opsional)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Hanya dipakai untuk membalas Anda. Tidak untuk hal lain.",
    submit: "Kirim masukan",
    sending: "Mengirim…",
    retry: "Coba lagi",
    privacyNote:
      "Formulir ini tidak terhubung ke perangkat mana pun. Isinya tidak memuat notifikasi, papan klip, atau berkas Anda. Email bersifat opsional dan hanya dipakai untuk membalas.",
    success: "Terima kasih. Setiap masukan dibaca oleh manusia.",
    errorGeneric: "Tidak terkirim. Pesan Anda masih ada di sini.",
    errorRateLimited:
      "Anda baru saja mengirim beberapa pesan. Coba lagi sekitar satu jam. Teks Anda tetap tersimpan di sini.",
    footerWebApp: "Aplikasi web Reflecto tetap berfungsi tanpa ekstensi.",
    footerWebAppLink: "Buka aplikasi web",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Tutup",
    aiSummaryTag: "Ringkasan AI",
    errorAi: "Tidak dapat membuat ringkasan saat ini.",
    retryCta: "Coba lagi",
    downloadHeading: "Dapatkan ringkasan AI",
    downloadBody:
      "Kejar ketinggalan dengan rangkuman AI di perangkat. Berjalan sepenuhnya di perangkat Anda.",
    downloadCta: "Unduh model",
    downloadPrivacyNote: "Unduhan satu kali. Server tidak pernah melihat notifikasi Anda.",
    downloadingHeading: "Mengunduh model…",
    caughtUp: "Anda sudah mengikuti semuanya — tidak ada yang memerlukan perhatian Anda saat ini.",
    needsYou: "Perlu perhatian Anda",
    quiet: "Lainnya",
    scanning: (pending: number): string => `Merangkum ${pending}…`,
    jump: "Buka notifikasi",
    affordanceReply: "Balas",
    affordanceOpen: "Buka",
    affordanceCopyOtp: "Salin kode",
    buttonLabel: "Glance",
  },

  update: {
    title: "Pembaruan tersedia",
    description: "Versi baru Reflecto sudah siap. Muat ulang untuk memperbarui.",
    reload: "Muat ulang",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Kami menamai perangkat ini “${name}”.`,
    cardLooksGood: "Oke",
    cardEdit: "Edit",
    cardAriaLabel: "Konfirmasi nama perangkat",
    renameTitle: "Ganti nama perangkat ini",
    renameLabel: "Nama perangkat",
    renameSave: "Simpan",
    renameCancel: "Batal",
    renameFailed: "Tidak dapat mengganti nama. Silakan coba lagi.",
  },

  collision: {
    title: (name: string): string => `Anda sudah memiliki “${name}”`,
    subtitle: "Apa nama untuk yang ini?",
    chipWork: (name: string): string => `Kerja ${name}`,
    chipHome: (name: string): string => `Rumah ${name}`,
    chipPersonal: (name: string): string => `Pribadi ${name}`,
    customLabel: "Nama khusus",
    save: "Simpan",
    useAnyway: "Tetap gunakan nama asli",
    saveFailed: "Tidak dapat menyimpan nama baru. Silakan coba lagi.",
    ariaLabel: "Selesaikan nama perangkat duplikat",
  },

  clipboard: {
    title: "Papan klip",
    emptyTitle: "Belum ada klip",
    empty: "Klip dari perangkat Anda yang lain muncul di sini. Ketuk untuk menyalin.",
    from: (label: string): string => `dari ${label}`,
    to: (label: string): string => `ke ${label}`,
    copy: "Salin ke papan klip",
    copied: "Disalin",
    reveal: "Tampilkan",
    hide: "Sembunyikan",
    maskedHint: "Konten disembunyikan — tekan Tampilkan untuk menampilkan",
    sensitiveChip: "Sensitif",
    cloudChip: "dari token API Anda",
    delete: "Hapus klip",
    sendPlaceholder: "Tempel teks untuk dikirim…",
    sendCta: "Kirim",
    sendSensitiveToggle: "Tandai sensitif",
    sendSuccess: (n: number): string =>
      `Terkirim ke ${n} ${selectPlural("id", n, { other: "perangkat" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Terkirim ke ${n}/${total} ${selectPlural("id", total, { other: "perangkat" })}.`,
    sendNoDevices: "Tidak ada perangkat yang disandingkan untuk dikirimi.",
    sendFailed: "Tidak dapat mengirim. Silakan coba lagi.",
    sendEmpty: "Tidak ada yang dikirim.",
    dockRecentHeading: "Terbaru",
    dockEmptyClipboard: "Papan klip Anda kosong.",
    clearAll: "Hapus semua",
    clearAllConfirm: "Ya, hapus semua",
    clearAllCancel: "Batal",
    shareTitle: "Kirim ke perangkat Anda",
    shareReview:
      "Tinjau apa yang Anda kirim. Konten dikirim ke perangkat Anda yang disandingkan, terenkripsi end-to-end.",
    shareSend: "Kirim",
    shareCancel: "Batal",
    shareNotPaired: "Sandingkan perangkat terlebih dahulu untuk mengirim teks yang dibagikan.",
    shareOpenApp: "Buka Reflecto",
    pasteFromClipboard: "Tempel dari papan klip",
    readFailed: "Papan klip tidak dapat dibaca — tempel di bawah ini.",
  },
  install: {
    title: "Instal Reflecto",
    body: "Tambahkan ke layar utama Anda untuk akses lebih cepat.",
    cta: "Instal",
    dismissAria: "Tutup prompt pemasangan",
  },
  nav: {
    home: "Beranda",
    clipboard: "Papan klip",
    files: "Berkas",
    ariaLabel: "Utama",
    fileHiddenPublicMode: "Disembunyikan selama mode privasi aktif.",
  },
  common: {
    loading: "Memuat",
  },
  errors: {
    notPaired: "Belum disandingkan",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} di ${os}`,
    webFallback: "Peramban Web",
  },

  palette: {
    placeholder: "Ketik perintah…",
    noResults: "Tidak ada perintah yang cocok",
    ariaLabel: "Palet perintah",
    clearAll: "Hapus semua notifikasi",
    glance: "Ringkasan Glance",
    cancelSnooze: "Batalkan jeda",
    copyClip: "Salin klip terakhir yang diterima",
  },

  fileTransfer: {
    sendTitle: "Kirim file",
    sendChooseFile: "Pilih file",
    sendDeviceLabel: "Kirim ke",
    sendPhoneFallbackName: "Ponsel",
    send: "Kirim file",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Menyiapkan…" : `Menyiapkan — ${pct}%`,
    sendDelivering: "Mengirim ke ponsel Anda…",
    sendWaitingAccept: "Menunggu ponsel Anda menerima…",
    sendWaitingAppOpen: "Buka Reflecto di ponsel Anda untuk menerima",
    sendWaitingAppOpenHint:
      "Ponsel Anda tidak dapat menerima notifikasi push, jadi berkas ini baru muncul saat Reflecto dibuka.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Mengunggah bagian ${partNumber} dari ${partCount}…`,
    sendDone: "Terkirim.",
    sendFailedRejected: "Ponsel Anda menolak file ini.",
    sendFailedNoAnswer: "Tidak ada jawaban dari ponsel Anda.",
    sendFailedUploadFailed: "Unggahan gagal. Silakan coba lagi.",
    sendFailedCancelled: "Dibatalkan.",
    sendFailedGeneric: "Terjadi kesalahan. Silakan coba lagi.",
    sendTooLarge: (maxMiB: number): string => `File terlalu besar (maks. ${maxMiB} MiB).`,
    sendKeepOpenHint: "Biarkan tab ini terbuka sampai transfer selesai.",
    sendAnother: "Kirim lagi",
    sendNoDevice: "Sandingkan ponsel terlebih dahulu untuk mengirim file.",
    sendCancel: "Batal",
    sendPeerProgress: (chunksDone: number): string =>
      `Ponsel telah menerima ${chunksDone} potongan.`,

    receiveTitle: "File masuk",
    receiveOfferFrom: (sender: string): string => `${sender} ingin mengirimi Anda file`,
    receiveAccept: "Terima",
    receiveDecline: "Tolak",
    receiveReceiving: (n: number, total: number): string => `Menerima bagian ${n} dari ${total}…`,
    receiveDone: "Tersimpan.",
    receiveFailed: "Transfer gagal.",
    receiveCancelled: "Dibatalkan.",
    receiveCancel: "Batal",

    errorChunkAuthFail: "Transfer gagal — data tidak dapat diverifikasi. Coba lagi.",
    errorHashMismatch: "Transfer gagal — file tidak lulus pemeriksaan integritas. Coba lagi.",
    errorStorageFull: "Ruang penyimpanan tidak cukup untuk transfer ini.",
    errorExpired: "Transfer kedaluwarsa.",
    errorQuotaExceeded: "Batas transfer harian tercapai — coba lagi besok.",
    errorInternal: "Transfer gagal — terjadi kesalahan.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct}%`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Tutup",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Progres transfer",
    outgoing: (n: number): string => `Keluar (${n})`,
    incoming: (n: number): string => `Masuk (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Terima semua",
    dropTitle: "Seret & lepas di sini",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `File hingga ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Seret & lepas di sini, atau pilih file untuk dikirim",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Langsung",
    transportDirectHint: "Dikirim antarperangkat — tidak pernah disimpan di server kami.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Lewat server",
    transportRelayHint:
      "Diteruskan lewat server kami, tetap terenkripsi ujung ke ujung — kami tidak bisa membacanya.",
    directBadge: "Langsung",
  },
  time: {
    justNow: "baru saja",
    minutesAgo: (count: number): string => `${count}m lalu`,
    hoursAgo: (count: number): string => `${count}j lalu`,
  },
} satisfies MessagesShape;
